import React from 'react';
import {
  Checkbox,
  DatePicker,
  Label,
  RadioButtonGroup,
  Textarea,
} from '@roger/ui-components';
import type { GoalLength } from '../../services/api/types.gen';
import { GOAL_TYPE_OPTIONS, type PushGoalFieldValues } from '../../utils/goals';

interface PushGoalFieldsProps {
  /** Unique per goal, so each row's checkbox label targets its own input. */
  idPrefix: string;
  values: PushGoalFieldValues;
  onChange: (values: PushGoalFieldValues) => void;
  /** The episode the copy lands in; the date picker stays inside it. */
  minDate: Date | undefined;
  maxDate: Date | undefined;
  endOfEpisodeLabel: string;
  /** Show why the values cannot be saved, under the field at fault. */
  showErrors?: boolean;
}

/**
 * The re-target fields a push offers for the copy landing in the next
 * episode: instruction, target date (or end of episode), and term. Shared by
 * the per-visit push (PushGoalDialog) and the per-goal editor in "Push goals
 * from previous episode", so both apply the same rules.
 */
const PushGoalFields: React.FC<PushGoalFieldsProps> = ({
  idPrefix,
  values,
  onChange,
  minDate,
  maxDate,
  endOfEpisodeLabel,
  showErrors = false,
}) => {
  const handleDateChange = (date: Date | undefined) => {
    let targetDate = date;
    if (date && minDate && date.getTime() < minDate.getTime()) {
      targetDate = minDate;
    } else if (date && maxDate && date.getTime() > maxDate.getTime()) {
      targetDate = maxDate;
    }
    onChange({ ...values, targetDate, isForEndOfEpisode: false });
  };

  const handleEndOfEpisodeChange = (checked: boolean | 'indeterminate') => {
    const isForEndOfEpisode = checked === true;
    onChange({
      ...values,
      isForEndOfEpisode,
      targetDate: isForEndOfEpisode && maxDate ? maxDate : values.targetDate,
    });
  };

  const endOfEpisodeId = `${idPrefix}-end-of-episode`;

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Instruction
        </p>
        <Textarea
          aria-label='Instruction'
          placeholder='Enter goal instruction...'
          value={values.instruction}
          onChange={e => onChange({ ...values, instruction: e.target.value })}
          textareaSize='default'
          className='min-h-[80px]'
        />
        {showErrors && !values.instruction.trim() && (
          <p className='text-xs text-destructive'>Instruction is required.</p>
        )}
      </div>

      <div className='space-y-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Target date for meeting a goal
        </p>
        <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
          <DatePicker
            date={values.targetDate}
            onDateChange={handleDateChange}
            datePickerSize='sm'
            typable
            showClearButton
            minDate={minDate}
            maxDate={maxDate}
            className='w-full sm:w-44'
          />
          <div className='flex items-center gap-2'>
            <Checkbox
              id={endOfEpisodeId}
              checked={values.isForEndOfEpisode}
              onCheckedChange={handleEndOfEpisodeChange}
              checkboxSize='sm'
            />
            <Label
              htmlFor={endOfEpisodeId}
              className='text-sm cursor-pointer select-none'
            >
              {endOfEpisodeLabel}
            </Label>
          </div>
          <div className='flex shrink-0'>
            <RadioButtonGroup
              value={values.length}
              onValueChange={val =>
                onChange({ ...values, length: val as GoalLength })
              }
              options={GOAL_TYPE_OPTIONS}
              orientation='horizontal'
            />
          </div>
        </div>
        {showErrors && !values.targetDate && (
          <p className='text-xs text-destructive'>Choose a target date.</p>
        )}
      </div>
    </div>
  );
};

export default PushGoalFields;
